import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { getTenantIdFromToken } from "@/utils/auth";
import { useAuth0 } from "@auth0/auth0-react";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import { getTenantDialog } from "@/config/tenantDialogs";
import { logger } from "@/lib/logger";
import { AlertTriangle } from "lucide-react";

interface TenantSpecificDialogProps {
  /** Optional: Override default session key suffix */
  sessionKeySuffix?: string;
}

function TenantSpecificDialog({
  sessionKeySuffix = "guidelines-dialog-conversation",
}: TenantSpecificDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const { isAuthenticated } = useAuth0();
  const getCachedToken = useCachedAccessToken();

  useEffect(() => {
    const checkAndShowDialog = async () => {
      if (!isAuthenticated) return;

      try {
        const tenantId = await getTenantIdFromToken(getCachedToken);

        if (!tenantId) return;

        // Check if this tenant has a configured dialog
        const dialogConfig = getTenantDialog(tenantId);

        if (!dialogConfig) return; // No dialog configured for this tenant

        // Check if dialog has been shown in this session
        const sessionKey = `tenant-${tenantId}-${sessionKeySuffix}-shown`;
        const hasBeenShown = sessionStorage.getItem(sessionKey);

        if (!hasBeenShown) {
          setCurrentTenantId(tenantId);
          setIsOpen(true);
        }
      } catch (error) {
        logger.warn("Failed to check for a tenant-specific dialog", {
          operation: "checkTenantDialog",
          error,
        });
      }
    };

    checkAndShowDialog();
  }, [isAuthenticated, getCachedToken, sessionKeySuffix]);

  const handleClose = () => {
    if (!currentTenantId) return;

    setIsOpen(false);

    // Mark dialog as shown in session storage
    const sessionKey = `tenant-${currentTenantId}-${sessionKeySuffix}-shown`;
    sessionStorage.setItem(sessionKey, "true");

    // Call the dialog's onClose callback if configured
    const dialogConfig = getTenantDialog(currentTenantId);
    dialogConfig?.onClose?.();
  };

  if (!currentTenantId || !isOpen) return null;

  const dialogConfig = getTenantDialog(currentTenantId);
  if (!dialogConfig) return null;

  const CustomContent = dialogConfig.customContent;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-warning shrink-0" />
            {dialogConfig.title}
          </DialogTitle>
          <DialogDescription>{dialogConfig.description}</DialogDescription>
        </DialogHeader>

        {CustomContent && (
          <div className="mt-4">
            <CustomContent />
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button onClick={handleClose}>Forstået</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TenantSpecificDialog;
